// src/pages/theme/components/FontSelection.tsx
import * as React from "react";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
  createStyles,
} from "@material-ui/core";
import { CustomTheme } from "../../../../../types/customTheme";
import { AVAILABLE_FONTS } from "../../../../../utils/consts";

const useStyles = makeStyles((theme: CustomTheme) =>
  createStyles({
    wrapper: {
      marginBottom: 16,
    },
    label: {
      margin: "4px 4px 4px 0",
      display: "flex",
      justifyContent: "start",
    },
    formControl: {
      width: "100%",
      borderRadius: "10px",
      backgroundColor: theme.palette.background.entity_highlight_background,
      [`& fieldset`]: {
        borderRadius: "10px",
        border: "none",
      },
    },
    previewBox: {
      marginTop: 8,
      padding: "10px 12px",
      borderRadius: "8px",
      border: `1px solid ${theme.palette.background.entity_border}`,
      minHeight: 28,
      overflow: "hidden",
    },
  }),
);

export interface FontSelectionProps {
  title: string;
  value: string;
  setValue: (value: string) => void;
  previewText?: string;
  fontWeight?: string;
  setFontWeight?: (value: string) => void;
  fontSize?: string;
  setFontSize?: (value: string) => void;
}

/* Dropdown for choosing a font, with a live text preview using the selected font. */
const FontSelection: React.FunctionComponent<FontSelectionProps> = ({
  title,
  value,
  setValue,
  previewText = "The quick brown fox jumps",
  fontWeight,
  setFontWeight,
  fontSize,
  setFontSize,
}) => {
  const classes = useStyles();
  const labelId = `font-select-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const weightId = `font-weight-${title.replace(/\s+/g, "-").toLowerCase()}`;
  const sizeId = `font-size-${title.replace(/\s+/g, "-").toLowerCase()}`;

  const resolvedFontFamily = value ? `${value}, sans-serif` : "inherit";

  return (
    <div className={classes.wrapper}>
      <InputLabel id={labelId} className={classes.label}>
        {title}
      </InputLabel>
      <FormControl variant="outlined" size="small" className={classes.formControl}>
        <Select
          labelId={labelId}
          value={value ?? ""}
          onChange={(e: any) => setValue(e.target.value)}
        >
          {AVAILABLE_FONTS.map((font) => (
            <MenuItem key={font.label} value={font.value}>
              <span
                style={{
                  fontFamily: font.value ? `${font.value}, sans-serif` : "inherit",
                }}
              >
                {font.label}
              </span>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className={classes.previewBox}>
        <Typography
          style={{
            fontFamily: resolvedFontFamily,
            fontWeight: fontWeight ? (fontWeight as any) : undefined,
            fontSize: fontSize ? `calc(1em + ${fontSize}px)` : undefined,
          }}
          noWrap
        >
          {previewText}
        </Typography>
      </div>

      {/* The font weight is typed in by hand and is sent together with the
          font, separated by a star (for example "Poppins*600"). */}
      {setFontWeight && (
        <>
          <InputLabel id={weightId} className={classes.label} style={{ marginTop: 10 }}>
            Font Weight
          </InputLabel>
          <TextField
            id={weightId}
            variant="outlined"
            size="small"
            className={classes.formControl}
            value={fontWeight ?? ""}
            onChange={(e: any) => setFontWeight(e.target.value)}
          />
        </>
      )}

      {/* The extra font size is typed in by hand and is sent after the font
          weight with a plus (for example "Poppins*600+2"). */}
      {setFontSize && (
        <>
          <InputLabel id={sizeId} className={classes.label} style={{ marginTop: 10 }}>
            Font Size
          </InputLabel>
          <TextField
            id={sizeId}
            variant="outlined"
            size="small"
            className={classes.formControl}
            value={fontSize ?? ""}
            onChange={(e: any) => setFontSize(e.target.value)}
          />
        </>
      )}
    </div>
  );
};

export default FontSelection;
